import { Box, Collapse, FormLabel, Divider, Flex, Spinner } from '@chakra-ui/react';
import { useState } from 'react';

import Loader from 'components/Loader';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import Title from 'components/Title';
import { fetchPaycorPaginatedEarnings } from 'hooks/integrations/paycor/useFetchPaycorPaginatedEarnings';
import { fetchPaycorPaginatedLegalEntities } from 'hooks/integrations/paycor/useFetchPaycorPaginatedLegalEntities';
import { useFetchPaycorTimeeroEarnings } from 'hooks/integrations/paycor/useFetchPaycorTimeeroEarnings';
import { useMapPaycorEarningCodes, IPaycorMapEarningCodes } from 'hooks/integrations/paycor/useMapPaycorEarningCodes';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

const EarningsMappings = () => {
  const [currentMap, setCurrentMap] = useState<string>('');
  const [entityId, setEntityId] = useState<any>('');

  const { status: earningCodeMapStatus, mutateAsync: earningCodeMapMutateAsync } = useMapPaycorEarningCodes();
  const { status: timeeroEarningsStatus, data: timeeroEarningData, refetch } = useFetchPaycorTimeeroEarnings(entityId);

  const loadPaycorEntitiesOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaycorPaginatedLegalEntities, { page, search });
  };

  const loadPaycorEarningOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaycorPaginatedEarnings, { page, search, legalEntityId: entityId });
  };

  const onLegalEntityChange = (entity: any) => {
    setEntityId(entity?.legal_entity_id);
  };

  const onEarningCodeSelected = (paycorEntity: any, timeeroCode: string) => {
    setCurrentMap(timeeroCode);
    const postData: IPaycorMapEarningCodes = {
      code_id: paycorEntity.id,
      timeero_code: timeeroCode,
      entityId: entityId,
    };
    earningCodeMapMutateAsync(postData).then(() => {
      refetch();
    });
  };

  return (
    <Box>
      <Title>Paycor Earnings Code</Title>
      <Box mt={8} maxWidth="300px">
        <FormLabel>Paycor Entity</FormLabel>
        <ReactSelectAsyncPaginate
          loadOptions={loadPaycorEntitiesOptions}
          additional={{
            page: 1,
          }}
          onChange={(value: any) => {
            onLegalEntityChange(value);
          }}
          size="md"
          variant="filled"
          fontSize="sm"
          width="100%"
          debounceTimeout={300}
          menuPortalTarget={document.body}
          isSearchable
          placeholder="Select an entity"
          getOptionLabel={(option: any) => option.entity_name}
          getOptionValue={(option: any) => option.legal_entity_id}
        />
      </Box>
      {entityId ? (
        timeeroEarningsStatus === 'success' ? (
          <Collapse in={true} animateOpacity>
            <Divider mt={6} mb={3} />
            <Box mb={3} maxWidth="300px">
              <FormLabel>Regular Earnings Code</FormLabel>
              <Box position="relative">
                {earningCodeMapStatus === 'loading' &&
                  currentMap === timeeroEarningData?.payload?.regular?.timeero_code && (
                    <Flex
                      width="100%"
                      height="100%"
                      position="absolute"
                      zIndex={2}
                      justifyContent="center"
                      alignItems="center"
                      background="rgba(255, 255, 255, 0.7)"
                    >
                      <Spinner size="xs" />
                    </Flex>
                  )}

                <ReactSelectAsyncPaginate
                  loadOptions={loadPaycorEarningOptions}
                  value={timeeroEarningData?.payload?.regular?.earnings}
                  additional={{
                    page: 1,
                  }}
                  onChange={(value: any) => {
                    onEarningCodeSelected(value, timeeroEarningData?.payload?.regular?.timeero_code);
                  }}
                  size="md"
                  variant="filled"
                  fontSize="sm"
                  width="100%"
                  debounceTimeout={300}
                  menuPortalTarget={document.body}
                  isSearchable
                  placeholder="Select regular earnings code"
                  getOptionLabel={(option: any) => option.description}
                  getOptionValue={(option: any) => option.id}
                />
              </Box>
            </Box>
            <Box mb={3} maxWidth="300px">
              <FormLabel>Overtime Earnings Code</FormLabel>
              <Box position="relative">
                {earningCodeMapStatus === 'loading' &&
                  currentMap === timeeroEarningData?.payload?.overtime?.timeero_code && (
                    <Flex
                      width="100%"
                      height="100%"
                      position="absolute"
                      zIndex={2}
                      justifyContent="center"
                      alignItems="center"
                      background="rgba(255, 255, 255, 0.7)"
                    >
                      <Spinner size="xs" />
                    </Flex>
                  )}
                <ReactSelectAsyncPaginate
                  loadOptions={loadPaycorEarningOptions}
                  additional={{
                    page: 1,
                  }}
                  value={timeeroEarningData?.payload?.overtime?.earnings}
                  onChange={(value: any) => {
                    onEarningCodeSelected(value, timeeroEarningData?.payload?.overtime?.timeero_code);
                  }}
                  size="md"
                  variant="filled"
                  fontSize="sm"
                  width="100%"
                  debounceTimeout={300}
                  menuPortalTarget={document.body}
                  isSearchable
                  placeholder="Select overtime earnings code"
                  getOptionLabel={(option: any) => option.description}
                  getOptionValue={(option: any) => option.id}
                />
              </Box>
            </Box>
            <Box mb={3} maxWidth="300px">
              <FormLabel>Double Overtime Earnings Code</FormLabel>
              <Box position="relative">
                {earningCodeMapStatus === 'loading' &&
                  currentMap === timeeroEarningData?.payload?.doubleovertime?.timeero_code && (
                    <Flex
                      width="100%"
                      height="100%"
                      position="absolute"
                      zIndex={2}
                      justifyContent="center"
                      alignItems="center"
                      background="rgba(255, 255, 255, 0.7)"
                    >
                      <Spinner size="xs" />
                    </Flex>
                  )}
                <ReactSelectAsyncPaginate
                  loadOptions={loadPaycorEarningOptions}
                  additional={{
                    page: 1,
                  }}
                  value={timeeroEarningData?.payload?.doubleovertime?.earnings}
                  onChange={(value: any) => {
                    onEarningCodeSelected(value, timeeroEarningData?.payload?.doubleovertime?.timeero_code);
                  }}
                  size="md"
                  variant="filled"
                  fontSize="sm"
                  width="100%"
                  debounceTimeout={300}
                  menuPortalTarget={document.body}
                  isSearchable
                  placeholder="Select double overtime earnings code"
                  getOptionLabel={(option: any) => option.description}
                  getOptionValue={(option: any) => option.id}
                />
              </Box>
            </Box>
            <Box mb={3} maxWidth="300px">
              <FormLabel>Mileage Earnings Code</FormLabel>
              <Box position="relative">
                {earningCodeMapStatus === 'loading' &&
                  currentMap === timeeroEarningData?.payload?.mileage?.timeero_code && (
                    <Flex
                      width="100%"
                      height="100%"
                      position="absolute"
                      zIndex={2}
                      justifyContent="center"
                      alignItems="center"
                      background="rgba(255, 255, 255, 0.7)"
                    >
                      <Spinner size="xs" />
                    </Flex>
                  )}
                <ReactSelectAsyncPaginate
                  loadOptions={loadPaycorEarningOptions}
                  additional={{
                    page: 1,
                  }}
                  value={timeeroEarningData?.payload?.mileage?.earnings}
                  onChange={(value: any) => {
                    onEarningCodeSelected(value, timeeroEarningData?.payload?.mileage?.timeero_code);
                  }}
                  size="md"
                  variant="filled"
                  fontSize="sm"
                  width="100%"
                  debounceTimeout={300}
                  menuPortalTarget={document.body}
                  isSearchable
                  placeholder="select mileage earnings code"
                  getOptionLabel={(option: any) => option.description}
                  getOptionValue={(option: any) => option.id}
                />
              </Box>
            </Box>
          </Collapse>
        ) : (
          <Loader />
        )
      ) : null}
    </Box>
  );
};
export default EarningsMappings;
